'use client'

import { useEffect, useState } from 'react'
import { Field, Label, Switch } from '@headlessui/react'
import { RefreshCw, Wallet, Sparkles } from 'lucide-react'
import { useTranslation } from "react-i18next";

export default function AutoFields({ onChange }) {
    const { t, i18n } = useTranslation(["user"]);
    const isEnglish = i18n.language === "en";

    const [autoRenewal, setAutoRenewal] = useState(false)
    const [useWallet, setUseWallet] = useState(false)

    useEffect(() => {
        onChange({ autoRenewal, useWallet })
    }, [autoRenewal, useWallet])

    const switchBase =
        "group relative inline-flex w-11 h-6 flex-none cursor-pointer rounded-full p-0.5 " +
        "ring-1 transition-all duration-300 ease-in-out " +
        "focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-400/30";

    const switchOn =
        "bg-gradient-to-r from-teal-500 to-teal-700 ring-teal-500/40 " +
        "shadow-md shadow-teal-500/40";

    const switchOff =
        "bg-slate-200 dark:!bg-zinc-700 ring-slate-300/60 dark:!ring-zinc-600 " +
        "hover:bg-slate-300 dark:hover:!bg-zinc-600";

    const knobBase =
        "pointer-events-none inline-block h-5 w-5 transform rounded-full " +
        "bg-white shadow-md ring-0 transition-all duration-300 ease-in-out " +
        "flex items-center justify-center";

    const rowBase =
        "group flex items-start gap-x-3 p-3.5 rounded-2xl " +
        "bg-white/60 dark:!bg-zinc-900/50 " +
        "backdrop-blur-md " +
        "ring-1 transition-all duration-300 ";

    const rowIdle =
        "ring-slate-200/70 dark:!ring-zinc-700 " +
        "hover:ring-teal-300/60 dark:hover:!ring-teal-700 " +
        "hover:shadow-[0_10px_30px_-18px_rgba(13,148,136,0.4)]";

    const rowActive =
        "ring-teal-400/60 dark:!ring-teal-500/40 " +
        "bg-gradient-to-br from-teal-50/70 to-amber-50/50 " +
        "dark:!from-teal-950/40 dark:!to-amber-950/20 " +
        "shadow-[0_12px_35px_-18px_rgba(13,148,136,0.5)]";

    return (
        <div className="bg-transparent px-0 py-1">

            <form action="#" method="POST" className="mx-auto mt-1 max-w-xl sm:mt-1">
                <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">

                    {/* ==================== Auto Renewal ==================== */}
                    <Field
                        className={`${rowBase} sm:col-span-2 ${autoRenewal ? rowActive : rowIdle}`}
                    >
                        {/* icon */}
                        <div
                            className={`flex-shrink-0 w-9 h-9 rounded-xl mt-0.5
                                        flex items-center justify-center
                                        transition-all duration-300
                                        ${autoRenewal
                                    ? "bg-gradient-to-br from-teal-500 to-teal-700 shadow-md shadow-teal-500/30"
                                    : "bg-slate-100 dark:!bg-zinc-800 ring-1 ring-slate-200/70 dark:!ring-zinc-700"
                                }`}
                        >
                            <RefreshCw
                                className={`w-4 h-4 transition-all duration-300
                                            ${autoRenewal
                                        ? "text-white group-hover:rotate-180"
                                        : "text-slate-500 dark:!text-zinc-400"}`}
                            />
                        </div>

                        <div className="flex-1 min-w-0 pt-1">
                            <Label
                                className={`block text-[13px] font-bold leading-6 cursor-pointer
                                            transition-colors duration-200
                                            ${autoRenewal
                                        ? "text-teal-800 dark:!text-teal-200"
                                        : "text-slate-700 dark:!text-zinc-200"}`}
                            >
                                {t("autoFields.autoRenewal.title")}
                            </Label>
                            <p className="text-[11px] leading-5 mt-0.5
                                          text-slate-400 dark:!text-zinc-500">
                                {t("autoFields.autoRenewal.desc")}
                            </p>
                        </div>

                        <div className="flex h-6 items-center mt-1.5">
                            <Switch
                                checked={autoRenewal}
                                onChange={setAutoRenewal}
                                className={`${switchBase} ${autoRenewal ? switchOn : switchOff}`}
                            >
                                <span className="sr-only">
                                    {t("autoFields.autoRenewal.srLabel")}
                                </span>
                                <span
                                    aria-hidden="true"
                                    className={`${knobBase} ${autoRenewal ? '-translate-x-5' : 'translate-x-0'}`}
                                >
                                    {autoRenewal && (
                                        <Sparkles className="w-2.5 h-2.5 text-teal-600" />
                                    )}
                                </span>
                            </Switch>
                        </div>
                    </Field>

                    {/* ==================== Use Wallet ==================== */}
                    <Field
                        className={`${rowBase} sm:col-span-2 ${useWallet ? rowActive : rowIdle}`}
                    >
                        {/* icon */}
                        <div
                            className={`flex-shrink-0 w-9 h-9 rounded-xl mt-0.5
                                        flex items-center justify-center
                                        transition-all duration-300
                                        ${useWallet
                                    ? "bg-gradient-to-br from-amber-400 to-amber-600 shadow-md shadow-amber-500/30"
                                    : "bg-slate-100 dark:!bg-zinc-800 ring-1 ring-slate-200/70 dark:!ring-zinc-700"
                                }`}
                        >
                            <Wallet
                                className={`w-4 h-4 transition-colors duration-300
                                            ${useWallet
                                        ? "text-white"
                                        : "text-slate-500 dark:!text-zinc-400"}`}
                            />
                        </div>

                        <div className="flex-1 min-w-0 pt-1">
                            <Label
                                className={`block text-[13px] font-bold leading-6 cursor-pointer
                                            transition-colors duration-200
                                            ${useWallet
                                        ? "text-amber-800 dark:!text-amber-200"
                                        : "text-slate-700 dark:!text-zinc-200"}`}
                            >
                                {t("autoFields.useWallet.title")}
                            </Label>
                            <p className="text-[11px] leading-5 mt-0.5
                                          text-slate-400 dark:!text-zinc-500">
                                {t("autoFields.useWallet.desc")}
                            </p>
                        </div>

                        <div className="flex h-6 items-center mt-1.5">
                            <Switch
                                checked={useWallet}
                                onChange={setUseWallet}
                                className={`${switchBase} ${useWallet
                                    ? "bg-gradient-to-r from-amber-400 to-amber-600 ring-amber-500/40 shadow-md shadow-amber-500/40"
                                    : switchOff
                                    }`}
                            >
                                <span className="sr-only">
                                    {t("autoFields.useWallet.srLabel")}
                                </span>
                                <span
                                    aria-hidden="true"
                                    className={`${knobBase} ${useWallet ? '-translate-x-5' : 'translate-x-0'}`}
                                >
                                    {useWallet && (
                                        <Sparkles className="w-2.5 h-2.5 text-amber-600" />
                                    )}
                                </span>
                            </Switch>
                        </div>
                    </Field>
                </div>
            </form>
        </div>
    )
}